/**
 * A template field's Title -- the label the filled-in form shows, for a system field (every kind's
 * declared fields) and for one the author added.
 *
 * The server's own rule for it (`TemplateFieldRequest.label`: `@NotBlank`, `@Size(max = 200)`), stated here so
 * the builder refuses what the save would, under the field, before a request is sent.
 */

/** `os_form_template_field.oftfld_label` is `varchar(200)`. */
export const TEMPLATE_FIELD_TITLE_MAX = 200;

/**
 * The most fields an author may add to one step (review sheet #28); a kind's own fields do not count. The server
 * refuses a sixth under `sections[i].fields` (`TemplateService.MAX_CUSTOM_FIELDS_PER_SECTION`).
 */
export const TEMPLATE_CUSTOM_FIELDS_PER_STEP_MAX = 5;

/** The message for a step at or over the limit, as the server words it. */
export const TEMPLATE_CUSTOM_FIELDS_LIMIT_MESSAGE = `Maximum ${String(TEMPLATE_CUSTOM_FIELDS_PER_STEP_MAX)} custom fields allowed per step.`;

/**
 * A template's own name: letters, digits, spaces and "&" (`SaveTemplateRequest.name`'s `@Pattern`, 2026-10-09 --
 * "IT & Software 2026"). Anything else is refused by the server under `name`, so the builder refuses it first.
 */
export const TEMPLATE_NAME_PATTERN = /^[\p{L}\p{M}\p{N} &]*$/u;

/** The server's message for a name outside `TEMPLATE_NAME_PATTERN`, word for word. */
export const TEMPLATE_NAME_PATTERN_MESSAGE = 'Template name may contain letters, digits, spaces and &';

/**
 * A template's own Description: at most 500 characters (`SaveTemplateRequest.description`, `@Size(max = 500)`).
 *
 * <h2>A business rule, not a column width</h2>
 *
 * `os_form_template.oft_description` is `text` and is deliberately left that way. Narrowing it would mean either
 * failing the migration on, or silently truncating, any description already stored over the limit — and the field
 * has never been validated, so there may well be some. The bound is enforced where it can refuse an edit and
 * explain itself: the form and the request.
 *
 * <h2>Optional</h2>
 *
 * The Description is not mandatory on any of the four kinds, and this does not make it so — a kind states its
 * required Template Information fields in `requiredInformation`, and Description appears in none of them. The
 * builder sends `null` for a blank one and the server stores null.
 */
export const TEMPLATE_DESCRIPTION_MAX = 500;

/** The message for a description over the limit, worded identically on both sides. */
export const TEMPLATE_DESCRIPTION_MAX_MESSAGE =
  `Description must be ${String(TEMPLATE_DESCRIPTION_MAX)} characters or fewer.`;

/** What is wrong with a template's Description, or null when nothing is. Blank is allowed: it is optional. */
export function templateDescriptionError(description: string): string | null {
  return description.trim().length > TEMPLATE_DESCRIPTION_MAX ? TEMPLATE_DESCRIPTION_MAX_MESSAGE : null;
}

/** What is wrong with a field's Title, or null when nothing is. Blank and whitespace-only are both empty. */
export function fieldTitleError(title: string): string | null {
  const trimmed = title.trim();
  if (trimmed === '') return 'Field label is required.';
  if (trimmed.length > TEMPLATE_FIELD_TITLE_MAX) {
    return `Title must be ${String(TEMPLATE_FIELD_TITLE_MAX)} characters or fewer.`;
  }
  return null;
}

/** A field's name as two names are compared for sameness: trimmed, spaces collapsed, case-blind. */
export function normaliseFieldTitle(title: string): string {
  return title.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** A name another field of the template already has (the PR Template, 2026-10-14). */
export function duplicateFieldTitleMessage(title: string): string {
  return `Field name "${title.trim()}" is already used in this template. Field names must be unique.`;
}

/** An added field with no type. The builder always sets one, but the row says so if it is ever lost. */
export function fieldTypeError(fieldType: string | null | undefined): string | null {
  return fieldType ? null : 'Please select a field type.';
}

/**
 * An added choice field -- Dropdown, Search and Select, Radio, a multi-select -- must offer something: the server
 * refuses one with no options (`sections[i].fields[j].options`). Blank rows do not count.
 */
export function fieldOptionsError(takesOptions: boolean, options: readonly string[]): string | null {
  return takesOptions && options.every((option) => option.trim() === '') ? 'Please add at least one option.' : null;
}

/** The fewest characters a template's name may have, trimmed (`SaveTemplateRequest.name`'s `@Size(min = 3)`). */
export const TEMPLATE_NAME_MIN = 3;

/** The message for a name under `TEMPLATE_NAME_MIN`, as the server words it. */
export const TEMPLATE_NAME_MIN_MESSAGE = `Template Name must be at least ${String(TEMPLATE_NAME_MIN)} characters.`;
